import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import type { PersonalityRate } from '../../types/damage';
import { individualValueEnumList } from '../../pokemon/pokemon';
import { rankCorrectionEnumList } from '../../types/stat';
import EffortSelectField from '../EffortSelectField';
import RankSelectField from '../RankSelectField';

export type StatKey = 'hp' | 'attack' | 'defense' | 'special_attack' | 'special_defense' | 'speed';
export type PersonalityStatKey = Exclude<StatKey, 'hp'>;
export type EffortKey = `${StatKey}_effort`;
export type PersonalityKey = `${PersonalityStatKey}_personality`;

export interface StatRowConfig {
  key: StatKey;
  label: string;
  showIv?: boolean;
  showPersonality?: boolean;
  showRank?: boolean;
}

interface HeaderLabels {
  stat: string;
  iv: string;
  effort: string;
  personality: string;
  rank: string;
}

interface ColumnWidths {
  stat?: number;
  iv?: number;
  effort?: number;
  personality?: number;
  rank?: number;
}

interface StatsInputTableCoreProps {
  rows: StatRowConfig[];
  effortValues: Record<EffortKey, number>;
  onEffortChange: (key: EffortKey, value: number) => void;
  effortMaxValue?: number;
  personalityValues?: Partial<Record<PersonalityKey, PersonalityRate>>;
  onPersonalityChange?: (key: PersonalityKey, value: PersonalityRate) => void;
  enableNatureConstraint?: boolean;
  ivValues?: Partial<Record<StatKey, 0 | 31>>;
  onIvChange?: (key: StatKey, value: 0 | 31) => void;
  rankValues?: Partial<Record<PersonalityStatKey, number>>;
  onRankChange?: (key: PersonalityStatKey, value: number) => void;
  variant?: 'sidebar' | 'card';
  headerLabels?: HeaderLabels;
  columnWidths?: ColumnWidths;
}

const PERSONALITY_KEY_MAP: Record<PersonalityStatKey, PersonalityKey> = {
  attack: 'attack_personality',
  defense: 'defense_personality',
  special_attack: 'special_attack_personality',
  special_defense: 'special_defense_personality',
  speed: 'speed_personality',
};

const DEFAULT_PERSONALITY_VALUES: Record<PersonalityStatKey, PersonalityRate> = {
  attack: 1.0,
  defense: 1.0,
  special_attack: 1.0,
  special_defense: 1.0,
  speed: 1.0,
};

const buildPersonalityValues = (
  personalityValues?: Partial<Record<PersonalityKey, PersonalityRate>>,
): Record<PersonalityStatKey, PersonalityRate> => ({
  attack: personalityValues?.attack_personality ?? DEFAULT_PERSONALITY_VALUES.attack,
  defense: personalityValues?.defense_personality ?? DEFAULT_PERSONALITY_VALUES.defense,
  special_attack: personalityValues?.special_attack_personality ?? DEFAULT_PERSONALITY_VALUES.special_attack,
  special_defense: personalityValues?.special_defense_personality ?? DEFAULT_PERSONALITY_VALUES.special_defense,
  speed: personalityValues?.speed_personality ?? DEFAULT_PERSONALITY_VALUES.speed,
});

function StatsInputTableCore({
  rows,
  effortValues,
  onEffortChange,
  effortMaxValue,
  personalityValues,
  onPersonalityChange,
  enableNatureConstraint = false,
  ivValues,
  onIvChange,
  rankValues,
  onRankChange,
  variant = 'sidebar',
  headerLabels,
  columnWidths,
}: StatsInputTableCoreProps) {
  const resolvedHeaderLabels: HeaderLabels = {
    stat: headerLabels?.stat ?? '能力',
    iv: headerLabels?.iv ?? '個体値',
    effort: headerLabels?.effort ?? '努力値',
    personality: headerLabels?.personality ?? '性格',
    rank: headerLabels?.rank ?? 'ランク',
  };

  const {
    attack_personality,
    defense_personality,
    special_attack_personality,
    special_defense_personality,
    speed_personality,
  } = personalityValues ?? {};

  const resolvedPersonalityValues = useMemo(
    () =>
      buildPersonalityValues({
        attack_personality,
        defense_personality,
        special_attack_personality,
        special_defense_personality,
        speed_personality,
      }),
    [attack_personality, defense_personality, special_attack_personality, special_defense_personality, speed_personality],
  );

  const [localPersonalityValues, setLocalPersonalityValues] = useState<Record<PersonalityStatKey, PersonalityRate>>(
    resolvedPersonalityValues,
  );

  useEffect(() => {
    if (!enableNatureConstraint) return;
    setLocalPersonalityValues(resolvedPersonalityValues);
  }, [enableNatureConstraint, resolvedPersonalityValues]);

  const tableStyles = useMemo(() => {
    if (variant === 'card') {
      return {
        container: { maxWidth: 400, margin: 'auto' },
        table: { minWidth: 350 },
        headCell: { fontSize: '0.75rem', padding: '0.125px' },
        rowLabelCell: { fontWeight: 'bold', padding: '0.125px' },
        bodyCell: { padding: '0.125px' },
        ivGap: 0.5,
        personalityGap: 0.5,
      };
    }
    return {
      container: { mt: 2 },
      table: { minWidth: 350, maxWidth: 400 },
      headCell: { fontWeight: 'bold', px: 0.125, py: 0.125, fontSize: '0.75rem' },
      rowLabelCell: { fontWeight: 'bold', px: 0.125, py: 0.125, fontSize: '0.75rem' },
      bodyCell: { px: 0.125, py: 0.125 },
      ivGap: 0.03125,
      personalityGap: 0.0625,
    };
  }, [variant]);

  const getPersonalityValue = (statKey: PersonalityStatKey): PersonalityRate => {
    if (enableNatureConstraint) {
      return localPersonalityValues[statKey] ?? 1.0;
    }
    return personalityValues?.[PERSONALITY_KEY_MAP[statKey]] ?? 1.0;
  };

  const handlePersonalityToggle = (statKey: PersonalityStatKey, target: PersonalityRate) => {
    if (!onPersonalityChange) return;

    if (!enableNatureConstraint) {
      const currentValue = getPersonalityValue(statKey);
      const nextValue = currentValue === target ? 1.0 : target;
      onPersonalityChange(PERSONALITY_KEY_MAP[statKey], nextValue);
      return;
    }

    const currentValue = localPersonalityValues[statKey] ?? 1.0;
    const nextValue = currentValue === target ? 1.0 : target;
    const updatedPersonalities: Record<PersonalityStatKey, PersonalityRate> = {
      ...localPersonalityValues,
    };

    if (nextValue === 1.1) {
      Object.keys(updatedPersonalities).forEach((key) => {
        const personalityKey = key as PersonalityStatKey;
        if (personalityKey !== statKey && updatedPersonalities[personalityKey] === 1.1) {
          updatedPersonalities[personalityKey] = 1.0;
        }
      });
    } else if (nextValue === 0.9) {
      Object.keys(updatedPersonalities).forEach((key) => {
        const personalityKey = key as PersonalityStatKey;
        if (personalityKey !== statKey && updatedPersonalities[personalityKey] === 0.9) {
          updatedPersonalities[personalityKey] = 1.0;
        }
      });
    }

    updatedPersonalities[statKey] = nextValue;
    setLocalPersonalityValues(updatedPersonalities);

    Object.keys(updatedPersonalities).forEach((key) => {
      const personalityKey = key as PersonalityStatKey;
      onPersonalityChange(PERSONALITY_KEY_MAP[personalityKey], updatedPersonalities[personalityKey]);
    });
  };

  const renderIvCell = (row: StatRowConfig) => {
    const shouldShowIv = row.showIv ?? true;
    if (!shouldShowIv || !onIvChange) {
      return <Typography variant="body2">-</Typography>;
    }
    const currentValue = ivValues?.[row.key] ?? 31;
    return (
      <Box sx={{ display: 'flex', gap: tableStyles.ivGap, justifyContent: 'center' }}>
        {individualValueEnumList.map((iv) => (
          <Box
            key={iv}
            onClick={() => onIvChange(row.key, iv)}
            sx={{
              cursor: 'pointer',
              padding: '1px 2px',
              border: '1px solid #ccc',
              borderRadius: 0.125,
              fontSize: '0.85rem',
              backgroundColor: currentValue === iv ? '#1976d2' : 'white',
              color: currentValue === iv ? 'white' : '#1976d2',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: currentValue === iv ? '#1565c0' : '#f5f5f5',
              },
            }}
          >
            {iv}
          </Box>
        ))}
      </Box>
    );
  };

  const renderPersonalityCell = (row: StatRowConfig) => {
    const shouldShowPersonality = row.showPersonality ?? row.key !== 'hp';
    if (!shouldShowPersonality || !onPersonalityChange) {
      return <Typography variant="body2">-</Typography>;
    }
    const personalityKey = row.key as PersonalityStatKey;
    const currentValue = getPersonalityValue(personalityKey);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: tableStyles.personalityGap,
          justifyContent: 'center',
        }}
      >
        <IconButton
          size="small"
          onClick={() => handlePersonalityToggle(personalityKey, 0.9)}
          sx={{
            width: 16,
            height: 16,
            fontSize: '0.6rem',
            border: '1px solid #555',
            borderRadius: 0.125,
            minWidth: 0,
            padding: 0,
            backgroundColor: currentValue === 0.9 ? '#1976d2' : 'white',
            color: currentValue === 0.9 ? 'white' : '#555',
            '&:hover': {
              backgroundColor: currentValue === 0.9 ? '#1565c0' : '#f5f5f5',
            },
          }}
        >
          -
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handlePersonalityToggle(personalityKey, 1.1)}
          sx={{
            width: 16,
            height: 16,
            fontSize: '0.6rem',
            border: '1px solid #555',
            borderRadius: 0.125,
            minWidth: 0,
            padding: 0,
            backgroundColor: currentValue === 1.1 ? '#1976d2' : 'white',
            color: currentValue === 1.1 ? 'white' : '#555',
            '&:hover': {
              backgroundColor: currentValue === 1.1 ? '#1565c0' : '#f5f5f5',
            },
          }}
        >
          +
        </IconButton>
      </Box>
    );
  };

  const renderRankCell = (row: StatRowConfig) => {
    const shouldShowRank = row.showRank ?? row.key !== 'hp';
    if (!shouldShowRank || !onRankChange) {
      return <Typography variant="body2">-</Typography>;
    }
    const rankKey = row.key as PersonalityStatKey;
    const currentRank = rankValues?.[rankKey] ?? 0;

    if (variant === 'sidebar') {
      return <RankSelectField value={currentRank} onChange={(value) => onRankChange(rankKey, value)} />;
    }

    return (
      <FormControl size="small" sx={{ minWidth: 40 }}>
        <Select
          value={currentRank}
          onChange={(event) => onRankChange(rankKey, Number(event.target.value))}
          sx={{
            fontSize: '0.8rem',
            '& .MuiSelect-select': {
              padding: '2px 4px',
              textAlign: 'center',
            },
          }}
        >
          {rankCorrectionEnumList.map((rank) => (
            <MenuItem key={rank} value={rank} sx={{ fontSize: '0.8rem', justifyContent: 'center' }}>
              {rank > 0 ? `+${rank}` : rank}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <TableContainer component={Paper} sx={tableStyles.container}>
      <Table size="small" sx={tableStyles.table}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...tableStyles.headCell,
                minWidth: columnWidths?.stat,
              }}
            >
              {resolvedHeaderLabels.stat}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tableStyles.headCell,
                minWidth: columnWidths?.iv,
              }}
            >
              {resolvedHeaderLabels.iv}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tableStyles.headCell,
                minWidth: columnWidths?.effort,
              }}
            >
              {resolvedHeaderLabels.effort}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tableStyles.headCell,
                minWidth: columnWidths?.personality,
              }}
            >
              {resolvedHeaderLabels.personality}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...tableStyles.headCell,
                minWidth: columnWidths?.rank,
              }}
            >
              {resolvedHeaderLabels.rank}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell sx={tableStyles.rowLabelCell}>{row.label}</TableCell>
              <TableCell align="center" sx={tableStyles.bodyCell}>
                {renderIvCell(row)}
              </TableCell>
              <TableCell align="center" sx={tableStyles.bodyCell}>
                <EffortSelectField
                  value={effortValues[row.key + '_effort' as EffortKey] ?? 0}
                  maxValue={effortMaxValue}
                  onChange={(value) => onEffortChange(row.key + '_effort' as EffortKey, value)}
                />
              </TableCell>
              <TableCell align="center" sx={tableStyles.bodyCell}>
                {renderPersonalityCell(row)}
              </TableCell>
              <TableCell align="center" sx={tableStyles.bodyCell}>
                {renderRankCell(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default StatsInputTableCore;
